/**
 * Small constructors for authoring a template scene.
 *
 * A template is a `SceneDoc` in normalised units, and the two things every template needs are a
 * device fitted into a band of the canvas and a caption. Writing those out in full is eight
 * lines of defaults each time and buries the design decision; these helpers keep a template
 * readable as a layout.
 *
 * They are deliberately thin. Anything they do not cover is written as a plain layer, so a
 * template is never limited by the kit.
 */
import { DEFAULT_DEVICE_BODY, FAMILY_FRAME_TOKEN } from '@keymaker/schema';
import type {
  Background,
  Box,
  DeviceBodyInput,
  DeviceLayer,
  Layer,
  ShapeLayer,
  TextLayer,
  TextStyle,
} from '@keymaker/schema';

/** The canvas a template's base scene is authored against. `resolveTemplate` overrides it. */
export const REFERENCE_CANVAS = { width: 1320, height: 2868 } as const;

/** The asset key a project's uploaded screenshot is bound to. */
export const CAPTURE = 'capture';

export function solid(color: string): Background {
  return { type: 'solid', color };
}

/** 0 degrees runs top→bottom, 90 runs left→right. */
export function gradient(stops: string[], angle = 180): Background {
  return {
    type: 'gradient',
    angle,
    stops: stops.map((color, index) => ({
      offset: stops.length === 1 ? 0 : index / (stops.length - 1),
      color,
    })),
  };
}

export function radial(stops: { offset: number; color: string }[], radius = 0.7, center = { x: 0.5, y: 0.5 }): Background {
  return { type: 'radial', center, radius, stops };
}

export interface TextSpec {
  id?: string;
  content: string;
  box: Box;
  /** Font size as a fraction of canvas height. */
  size: number;
  color?: string;
  weight?: TextStyle['weight'];
  font?: string;
  align?: TextLayer['align'];
  valign?: TextLayer['valign'];
  lineHeight?: number;
  letterSpacing?: number;
  fit?: TextLayer['fit'];
  uppercase?: boolean;
  rotation?: number;
  opacity?: number;
  name?: string;
  /** Layer ids this caption is meant to overlap, for a deliberate full-bleed layout. */
  allowOverlapWith?: string[];
}

export function text(spec: TextSpec): TextLayer {
  return {
    type: 'text',
    id: spec.id ?? 'caption',
    name: spec.name,
    visible: true,
    locked: false,
    opacity: spec.opacity ?? 1,
    content: spec.content,
    style: {
      fontFamily: spec.font ?? 'Inter',
      size: spec.size,
      weight: spec.weight ?? 700,
      color: spec.color ?? '#FFFFFF',
      lineHeight: spec.lineHeight ?? 1.08,
      letterSpacing: spec.letterSpacing ?? -0.01,
      italic: false,
      uppercase: spec.uppercase ?? false,
    },
    box: spec.box,
    align: spec.align ?? 'left',
    valign: spec.valign ?? 'top',
    fit: spec.fit ?? 'shrink',
    rotation: spec.rotation ?? 0,
    allowOverlapWith: spec.allowOverlapWith ?? [],
  };
}

/** A 3D body styling, with every field optional — the kit fills the defaults. */
export type BodyStylingInput = DeviceBodyInput;

export interface DeviceSpec {
  id?: string;
  /** Defaults to `@family`, which resolves to each family's own bezel. */
  frame?: string;
  screen?: string;
  variant?: string;
  screenFit?: DeviceLayer['screenFit'];
  fitRegion?: DeviceLayer['fitRegion'];
  /** Used only when `fitRegion` is absent. */
  scale?: number;
  x?: number;
  y?: number;
  anchor?: DeviceLayer['transform']['anchor'];
  rotation?: number;
  /** Only meaningful for `kind: "3d"`. A subset is enough; the rest defaults. */
  body?: BodyStylingInput;
  /** Set to `"3d"` to render a real device body on the GPU instead of compositing a bezel. */
  kind?: DeviceLayer['kind'];
  perspective?: DeviceLayer['perspective'];
  shadow?: DeviceLayer['shadow'];
  opacity?: number;
  name?: string;
}

export function device(spec: DeviceSpec = {}): DeviceLayer {
  return {
    type: 'device',
    id: spec.id ?? 'device',
    name: spec.name,
    visible: true,
    locked: false,
    opacity: spec.opacity ?? 1,
    kind: spec.kind ?? 'frame',
    frame: spec.frame ?? FAMILY_FRAME_TOKEN,
    screen: spec.screen ?? CAPTURE,
    screenFit: spec.screenFit ?? 'cover',
    variant: spec.variant,
    transform: {
      x: spec.x ?? 0.5,
      y: spec.y ?? 0.5,
      scale: spec.scale ?? 1,
      rotation: spec.rotation ?? 0,
      anchor: spec.anchor ?? 'center',
    },
    fitRegion: spec.fitRegion,
    perspective: spec.perspective,
    // Filled here because zod's `.default()` makes every field required in the output type, so a
    // template author writing `body: { caseThickness: 0.02 }` would not typecheck without this.
    body: spec.body ? { ...DEFAULT_DEVICE_BODY, ...spec.body } : undefined,
    shadow: spec.shadow,
  };
}

/** A device fitted into a band, which is what most templates want. */
export function fittedDevice(
  spec: Omit<DeviceSpec, 'fitRegion' | 'scale' | 'x' | 'y' | 'anchor'> & {
    top: number;
    bottom?: number;
    /** Equal inset on both sides. */
    side?: number;
    /** Asymmetric insets, for "text left, device right" layouts. */
    left?: number;
    right?: number;
    align?: 'center' | 'bottom';
    maxScale?: number;
  },
): DeviceLayer {
  const { top, bottom, side, left, right, align, maxScale, ...rest } = spec;
  return device({
    ...rest,
    fitRegion: {
      top,
      bottom: bottom ?? 1,
      side: side ?? 0,
      left,
      right,
      align: align ?? 'bottom',
      maxScale: maxScale ?? 1,
    },
  });
}

export interface ShapeSpec {
  id: string;
  kind: ShapeLayer['kind'];
  box: Box;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  radius?: number;
  rotation?: number;
  opacity?: number;
  name?: string;
}

export function shape(spec: ShapeSpec): ShapeLayer {
  return {
    type: 'shape',
    id: spec.id,
    name: spec.name,
    visible: true,
    locked: false,
    opacity: spec.opacity ?? 1,
    kind: spec.kind,
    box: spec.box,
    radius: spec.radius ?? 0,
    fill: spec.fill,
    stroke: spec.stroke,
    strokeWidth: spec.strokeWidth ?? 0,
    rotation: spec.rotation ?? 0,
  };
}

/**
 * The lowest band of the canvas that a device may occupy, given a caption block.
 *
 * Centralised so every template leaves the same breathing room below a headline: a gap that
 * looks right on one family is the difference between a tight layout and a collision on
 * another, and the pure-geometry test suite checks it per family.
 */
export const SAFE_GAP = 0.025;

export function bandBelow(caption: { y: number; height: number }): number {
  return Math.min(caption.y + caption.height + SAFE_GAP, 0.9);
}

export function layers(...entries: (Layer | Layer[])[]): Layer[] {
  return entries.flat();
}
